/**
 * The data the server already had, so React's first render is not empty.
 *
 * THE PROBLEM IT SOLVES
 *
 * Every public page gets its brand from an effect, and effects do not run
 * under renderToString. Rendered on the server as-is, the whole app is the
 * `minHeight: 100vh` placeholder BrandedOrFoundation shows while it waits:
 * 212 bytes and not one word.
 *
 * THE PART THAT MATTERS FOR HYDRATION
 *
 * React only adopts server HTML if the client's FIRST render produces the
 * same tree. So this is not only about giving the server something to draw:
 * the browser has to start from the identical data, or React throws the
 * server's DOM away and the whole exercise is undone. One value, read the
 * same way on both sides.
 *
 * WHAT IS NOT IN HERE, DELIBERATELY
 *
 * Listings and stats. Those arrive in an effect on both sides, so the
 * server's first render and the browser's first render agree about not
 * having them yet, and the cards appear afterwards on both. Seeding them
 * would mean the server rendered cards the client's first pass did not,
 * which is the mismatch this exists to avoid. They also sit below the fold,
 * so they are not what anybody is waiting to see.
 */

// On the server there is no window, so the entry sets this before rendering.
let serverData = null;

export function setServerData(data) {
  serverData = data || null;
}

/** The injected payload, whichever side we are on. */
export function initialData() {
  if (typeof window === "undefined") return serverData;
  return window.__FDN_INITIAL__ || null;
}

/** The agency's record, if the server had already resolved it. */
export function initialBrand() {
  const d = initialData();
  return (d && d.brand) || null;
}

/** The listing the server already fetched, if this is a property page.
 *
 * The exception to "only the brand". A property page's largest paint is the
 * first photograph of its gallery, and that photograph does not exist until
 * the listing has been fetched — so a server render without it draws the
 * loading state and is worth nothing. Seeding it is what lets the server
 * draw the gallery, with the <img> and its srcset, in the HTML.
 *
 * It is only ever the listing at THIS path: EstateProperty checks the slug
 * it was given against the one seeded, so a client-side navigation to a
 * different house cannot be handed the previous one.
 */
export function initialProperty() {
  const d = initialData();
  return (d && d.property) || null;
}

/** The absolute URL being rendered. `window.location` in a browser.
 *
 * SeoHead builds the canonical from it. Without this the server renders no
 * canonical at all while the browser's first render renders one: a missing
 * tag on every server-rendered page, and a mismatch on top.
 */
export function currentUrl() {
  if (typeof window !== "undefined") {
    return window.location.href.split("?")[0].split("#")[0];
  }
  const d = initialData();
  return (d && d.url) || "";
}

/** Just the path of currentUrl(). Same reason: a breadcrumb built during
 * render cannot read window.location on a server. */
export function canonicalPath() {
  const url = currentUrl();
  if (!url) return "";
  const i = url.indexOf("/", url.indexOf("//") + 2);
  return i === -1 ? "/" : url.slice(i);
}

/** Whether this page was server-rendered, which is what decides hydrate vs render. */
export function wasServerRendered() {
  const d = initialData();
  return !!(d && d.ssr);
}
